import {Inject, Injectable} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  first,
  map,
  Observable,
  shareReplay,
  tap,
  withLatestFrom
} from "rxjs";
import {BusinessPlanApi} from "@modules/account-settings/pages/business-profile/api/business-plan.api";
import {BusinessPlan} from "@modules/account-settings/pages/business-profile/models/business-plan.model";
import {User} from "@modules/user/models/user.model";
import {PaymentType, PaymentTypeName} from "@modules/payment/models/payment-type.model";
import {ACTIVE_USER} from "@modules/auth/providers/auth.provider";

@Injectable({
  providedIn: 'root'
})
export class BusinessPlanService {
  get isPremiumPlan$(): Observable<boolean> {
    return this.businessPlan$.pipe(
      map(plan => plan && plan.isPremium())
    )
  }

  set businessPlan(value: BusinessPlan) {
    this.setBusinessPlan(value);
  }

  private readonly _businessPlan$ = new BehaviorSubject<BusinessPlan>(null);
  private readonly _businessPlanInitialized$ = new BehaviorSubject(false);
  private businessPlanRequest$: Observable<BusinessPlan> | null = null;

  readonly businessPlan$: Observable<BusinessPlan> = this._businessPlan$.asObservable()
  readonly businessPlanInitialized$: Observable<boolean> = this._businessPlanInitialized$.asObservable()

  constructor(
    @Inject(ACTIVE_USER) protected readonly activeUser$: Observable<User>,
    private readonly businessPlanApi: BusinessPlanApi,
  ) {
  }

  setBusinessPlan(value: BusinessPlan | null): void {
    this._businessPlan$.next(value);
    this._businessPlanInitialized$.next(true);
  }

  ensureLoaded$(): Observable<BusinessPlan> {
    if (this._businessPlanInitialized$.value) {
      return this.businessPlan$.pipe(first());
    }

    return this.fetchBusinessPlan$();
  }

  ensureFresh$(): Observable<BusinessPlan> {
    return this.fetchBusinessPlan$();
  }

  visiblePayment$(): Observable<boolean> {
    return this.businessPlan$.pipe(
      map(value => value && value.isPremium())
    )
  }

  visiblePaymentForActiveUser$(): Observable<boolean> {
    return this.visiblePayment$().pipe(
      withLatestFrom(this.activeUser$),
      map(([visiblePayment, user]) => user && !user.isExpert() && visiblePayment)
    )
  }

  visiblePrice(paymentType: PaymentTypeName, visiblePayment: boolean = false): boolean {
    return !PaymentType.isContactToClient(paymentType as PaymentTypeName) && visiblePayment;
  }

  private fetchBusinessPlan$(): Observable<BusinessPlan> {
    if (this.businessPlanRequest$) {
      return this.businessPlanRequest$;
    }

    const fallback$ = this.businessPlan$.pipe(first());
    const request$ = this.businessPlanApi.get$().pipe(
      first(),
      tap(plan => this.setBusinessPlan(plan)),
      catchError((e) => {
        console.error(e);

        return fallback$;
      }),
      finalize(() => {
        this.businessPlanRequest$ = null;
      }),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.businessPlanRequest$ = request$;

    return request$;
  }
}
